import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import WpDynamicTable from "../../Components/WhatsappTable/WpDynamicTable";
import {
    EInvoice_EWaySetupMain_add,
    EInvoice_EWaySetupMain_Update,
    EInvoice_EWaySetupMain_Delete,
    fetchEInvoiceEWaySetupMainData
} from "../../Redux/Einvoice&EwayBillSlice/Einvoice&EwayBill";
import Loader from "../../Components/Loader/Loader";

const Einvoice_Eway_Setup_Main = () => {
    const dispatch = useDispatch();

    const { EInvoiceEWaySetupMainData, EInvoiceEWaySetupMainLoading } = useSelector((state) => state.EinvoiceEwayBill);

    const [formData, setFormData] = useState({
        ProviderName: "",
        ClientKey: "",
        ClientSecretKey: "",
        TokenExpireTime: "",
        URL: "",
    });

    useEffect(() => {
        dispatch(fetchEInvoiceEWaySetupMainData());
    }, [dispatch]);

    const [editId, setEditId] = useState(null);
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validate = (formData) => {
        const errors = {};

        if (!formData.ProviderName || formData.ProviderName.trim() === "") {
            errors.ProviderName = "Provider Name is required";
        }

        if (!formData.ClientKey || formData.ClientKey.trim() === "") {
            errors.ClientKey = "Client Key is required";
        }

        if (!formData.ClientSecretKey || formData.ClientSecretKey.trim() === "") {
            errors.ClientSecretKey = "Client Secret Key is required";
        }

        if (!formData.TokenExpireTime || String(formData.TokenExpireTime).trim() === "") {
            errors.TokenExpireTime = "Token Expire Time is required";
        } else if (isNaN(formData.TokenExpireTime)) {
            errors.TokenExpireTime = "Token Expire Time must be a number";
        }

        if (!formData.URL || formData.URL.trim() === "") {
            errors.URL = "URL is required";
        } else {
            try {
                new URL(formData.URL);
            } catch (err) {
                errors.URL = "Invalid URL format";
            }
        }

        return errors;
    };

    const handleEdit = (row) => {
        setEditId(row.MainId);
        setFormData({
            ProviderName: row.ProviderName,
            ClientKey: row.ClientKey,
            ClientSecretKey: row.ClientSecretKey,
            TokenExpireTime: row.TokenExpireTime,
            URL: row.URL,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate(formData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        if (Object.keys(validationErrors).length === 0) {
            try {
                if (editId) {
                    await dispatch(EInvoice_EWaySetupMain_Update({ id: editId, formData })).unwrap();
                    toast.success("Einvoice/Eway Setup Data updated successfully!", {
                        position: "top-right",
                        autoClose: 1000,
                    });
                } else {
                    await dispatch(EInvoice_EWaySetupMain_add({ values: formData })).unwrap();
                    toast.success("Einvoice/Eway Setup Data added successfully!!", {
                        position: "top-right",
                        autoClose: 1000,
                    });
                }

                // Reset form
                setFormData({
                    ProviderName: "",
                    ClientKey: "",
                    ClientSecretKey: "",
                    TokenExpireTime: "",
                    URL: "",
                });
                dispatch(fetchEInvoiceEWaySetupMainData());
            } catch (error) {
                toast.error(
                    `Failed to ${editId ? "update" : "add"} Data: ${error.message || "Unknown error"
                    }`,
                    {
                        position: "top-right",
                        autoClose: 1000,
                    }
                );
            }
        } else {
            toast.error("Please fix validation errors.", {
                position: "top-right",
                autoClose: 1000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
            });
        }
    };

    const handleDelete = async (row) => {
        const isConfirmed = window.confirm(
            `Are you sure you want to delete this provider's setup?`
        );
        if (!isConfirmed) return;

        try {
            await dispatch(EInvoice_EWaySetupMain_Delete({ id: row.MainId })).unwrap();
            toast.success("Data deleted successfully!", {
                position: "top-right",
                autoClose: 1000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
            });
            dispatch(fetchEInvoiceEWaySetupMainData());
        } catch (error) {
            toast.error(`Failed to delete Data: ${error}`, {
                position: "top-right",
                autoClose: 1000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
            });
        }
    };

    const columns = [
        { field: "id", headerName: "No", flex: 1 },
        {
            field: "ProviderName",
            headerName: "Provider Name",
            flex: 1,
        },
        { field: "ClientKey", headerName: "Client Key", flex: 1 },
        { field: "ClientSecretKey", headerName: "Client Secret Key", flex: 1 },
        { field: "TokenExpireTime", headerName: "Token Expire Time", flex: 1 },
        { field: "URL", headerName: "URL", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            renderCell: (params) => (
                <div className="flex space-x-2 justify-center">
                    <button
                        className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                        onClick={() => handleEdit(params.row)}
                    >
                        Update
                    </button>
                    <button
                        className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                        onClick={() => handleDelete(params.row)}
                    >
                        Delete
                    </button>
                </div>
            ),
        },
    ];

    const rows = Array?.isArray(EInvoiceEWaySetupMainData) ? EInvoiceEWaySetupMainData?.map((el, index) => ({
        id: index + 1,
        MainId: el._id,
        ProviderName: el?.ProviderName,
        ClientKey: el?.ClientKey,
        ClientSecretKey: el?.ClientSecretKey,
        TokenExpireTime: el?.TokenExpireTime,
        URL: el?.URL,
    })) : [];

    if (EInvoiceEWaySetupMainLoading && !EInvoiceEWaySetupMainData?.length) {
        return <Loader />;
    }

    return (
        <>
            <div className="">
                <div className="title">
                    <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                        {editId ? "Update E-invoice & E-way Setup Main" : "Add E-invoice & E-way Setup Main"}
                    </h3>
                </div>

                <div className="Form_bg_color bg-white shadow-sm rounded-xl mt-4">
                    <form
                        onSubmit={handleSubmit}
                        className="grid grid-cols-12 gap-4 p-3 rounded-2xl"
                    >
                        {/* Provider Name Input */}
                        <div className="col-span-12 lg:col-span-4 md:col-span-6">
                            <h5 className="mb-1 block font-semibold">
                                Provider Name
                            </h5>
                            <input
                                type="text"
                                name="ProviderName"
                                value={formData.ProviderName}
                                onChange={handleChange}
                                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
                            />
                            {errors.ProviderName && (
                                <p className="text-red-500 text-sm mt-1">
                                    {errors.ProviderName}
                                </p>
                            )}
                        </div>

                        {/* Client Key Input */}
                        <div className="col-span-12 lg:col-span-4 md:col-span-6">
                            <h5 className="mb-1 block font-semibold">Client Key</h5>
                            <input
                                type="text"
                                name="ClientKey"
                                value={formData.ClientKey}
                                onChange={handleChange}
                                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
                            />
                            {errors.ClientKey && (
                                <p className="text-red-500 text-sm mt-1">
                                    {errors.ClientKey}
                                </p>
                            )}
                        </div>

                        {/* Client Secret Key Input */}
                        <div className="col-span-12 lg:col-span-4 md:col-span-6">
                            <h5 className="mb-1 block font-semibold">Client Secret Key</h5>
                            <input
                                type="text"
                                name="ClientSecretKey"
                                value={formData.ClientSecretKey}
                                onChange={handleChange}
                                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
                            />
                            {errors.ClientSecretKey && (
                                <p className="text-red-500 text-sm mt-1">{errors.ClientSecretKey}</p>
                            )}
                        </div>

                        {/* Token Expire Time Input */}
                        <div className="col-span-12 lg:col-span-4 md:col-span-6">
                            <h5 className="mb-1 block font-semibold">Token Expire Time</h5>
                            <input
                                type="number"
                                name="TokenExpireTime"
                                value={formData.TokenExpireTime}
                                onChange={handleChange}
                                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
                            />
                            {errors.TokenExpireTime && (
                                <p className="text-red-500 text-sm mt-1">{errors.TokenExpireTime}</p>
                            )}
                        </div>

                        {/* URL Input */}
                        <div className="col-span-12 lg:col-span-4 md:col-span-6">
                            <h5 className="mb-1 block font-semibold">URL</h5>
                            <input
                                type="text"
                                name="URL"
                                value={formData.URL}
                                onChange={handleChange}
                                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
                            />
                            {errors.URL && (
                                <p className="text-red-500 text-sm mt-1">{errors.URL}</p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <div className="submit_Form col-span-12 flex justify-center mt-3">
                            <button type="submit" className="All_button">
                                {editId ? "Update" : "Submit"}
                            </button>

                            {editId && (
                                <button
                                    type="button"
                                    className="bg-gray-500 text-white ms-2 px-4 py-2 rounded hover:bg-gray-600"
                                    onClick={() => {
                                        setFormData({
                                            ProviderName: "",
                                            ClientKey: "",
                                            ClientSecretKey: "",
                                            TokenExpireTime: "",
                                            URL: "",
                                        });
                                        setEditId(null);
                                        setErrors({});
                                    }}
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                <div className="px-2 py-6">
                    <div className="title">
                        <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block text-uppercase rounded">
                            View E-invoice & E-way Setup Main
                        </h3>
                    </div>
                    <div className="rounded-lg overflow-x-auto mt-3">
                        <WpDynamicTable columns={columns} rows={rows} height="450px" />
                    </div>
                </div>
            </div>
        </>
    );
};

export default Einvoice_Eway_Setup_Main;